import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import NDAGate from "./NDAGate";
import { ForcePasswordChange } from "./ForcePasswordChange";

const _ROLE_HOMES = {
  funder: "/funder/onboarding",
  admin:  "/admin/dashboard",
  advisor: "/advisor/dashboard",
  rm: "/rm/dashboard",
};
const _homeForRole = (role) => _ROLE_HOMES[role] || "/founder/dashboard";

export default function ProtectedRoute({ children, role, skipNda, skipPathway, skipFunderOnboarding, skipFunderApproval }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F4F8F8]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#0A8B85] border-t-transparent" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Temporary-password accounts must set a real password before anything else.
  if (user.must_change_password) return <ForcePasswordChange />;
  // `role` accepts either a single role string or an array of allowed roles
  // (e.g. role={["funder","admin"]} so admins can inspect funder views).
  if (role) {
    const allowed = Array.isArray(role) ? role : [role];
    if (!allowed.includes(user.role)) {
      return <Navigate to={_homeForRole(user.role)} replace />;
    }
  }
  // Funders MUST confirm their mandate (investment profile) FIRST — even
  // while approval is pending — so every approval request reaches the admin
  // WITH journey data. /funder/onboarding route carries skipFunderApproval.
  if (
    user.role === "funder" &&
    !user.mandate_confirmed_at &&
    !skipFunderOnboarding &&
    location.pathname !== "/funder/onboarding"
  ) {
    return <Navigate to="/funder/onboarding" replace />;
  }
  // Funders must clear admin approval before reaching the platform.
  if (
    user.role === "funder" &&
    !skipFunderApproval &&
    (user.funder_approval_status === "pending" ||
     user.funder_approval_status === "rejected") &&
    location.pathname !== "/funder/approval"
  ) {
    return <Navigate to="/funder/approval" replace />;
  }
  // Business founders are no longer forced through the Get Started quiz — they go
  // straight to the NDA, then the pathway choice. Project-pathway founders still
  // complete their own project quiz first.
  if (
    user.role === "founder" &&
    user.company_kind === "project" &&
    !user.project_quiz_completed_at
  ) {
    return <Navigate to="/get-started-project?required=1" replace />;
  }
  // Founders MUST accept NDA before reaching any protected page
  if (user.role === "founder" && !skipNda) {
    return <NDAGate><PathwayGate skip={skipPathway}>{children}</PathwayGate></NDAGate>;
  }
  return children;
}

// Shown once: business founders pick "apply direct" vs "guided report" right after the NDA.
function PathwayGate({ skip, children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (skip || !user || user.role !== "founder" || user.company_kind === "project") return children;
  if (!user.pathway_chosen_at && location.pathname !== "/founder/pathway") {
    return <Navigate to="/founder/pathway" replace />;
  }
  return children;
}
